import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { MonthYearPicker, Badge, Pagination, Button, Avatar, LoadingSpinner, confirmAlert } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { FaPlus, FaTrash, FaSync, FaPaperPlane, FaTimes, FaCalendarAlt, FaDownload, FaSearch } from 'react-icons/fa';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import SalesUnitSelect from '../../../components/app/crm/release-forecast/components/SalesUnitSelect';
import StatusSelect, { StatusBadge } from '../../../components/app/crm/release-forecast/components/StatusSelect';
import { AgGridReact } from 'ag-grid-react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Link } from 'react-router-dom';
import ActionCell from '../../../components/app/crm/release-forecast/components/ActionCell';
import ReleaseConfirmDialog from '../../../components/app/crm/release-forecast/sales/ReleaseConfirmDialog';
import SalesBuildingSelectModal from '../../../components/app/crm/release-forecast/sales/SalesBuildingSelectModal';
import SalesForecastEditForm from '../../../components/app/crm/release-forecast/sales/SalesForecastEditForm';
import {
  getSalesForecasts,
  deleteSalesForecast,
  releaseSalesToCsd,
  setSalesSort,
  exportSalesForecast
} from 'redux/crm/releaseForecastSlice';
import { formatDate, formatNumber } from 'helpers/utils';
import { toast } from 'react-toastify';
import { getForecastMonthLabels, getMonthColorStyles } from '../../../components/app/crm/release-forecast/components/helpers';
import useDebouncedForecastSearch from '../../../components/app/crm/release-forecast/components/useDebouncedForecastSearch';

dayjs.extend(customParseFormat);

const monthTotal = (buildings, month) =>
  (buildings || [])
    .filter((b) => b.month === month)
    .reduce((sum, b) => sum + (parseFloat(b.weight) || 0), 0);

const getDefaultPeriod = () => {
  // Check for period in URL params first
  const params = new URLSearchParams(window.location.search);
  const periodParam = params.get('period');
  return periodParam || dayjs().format('MMM-YYYY');
};

const ReleaseForecastSalesPage = () => {
  const dispatch = useDispatch();
  const [pageReady, setPageReady] = useState(false);

  // CSS for MT Released header group
  const headerGroupStyle = `
    .ag-header-group-cell.mt-released-group {
      background-color: #a1bbf0 !important;
      font-weight: 600 !important;
      color: #1e40af !important;
      text-align: center !important;
    }
    .ag-header-cell.mt-released-child {
      background-color: #a1bbf0 !important;
      font-weight: 600 !important;
      color: #1e40af !important;
    }
  `;

  const [showSelectModal, setShowSelectModal] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [releaseTarget, setReleaseTarget] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize,    setPageSize]    = useState(20);

  const [filters, setFilters] = useState({
    period: getDefaultPeriod(),
    sales_units: [],
    status: '',
  });
  const { searchInput, setSearchInput, searchQuery, clearSearch } = useDebouncedForecastSearch();

  const [allExpanded, setAllExpanded] = useState(false);

  const salesState    = useSelector((state) => state.crm_release_forecast.salesData);
  const exportLoading = useSelector((state) => state.crm_release_forecast.exportLoading);
  const gridRef = useRef(null);

  const buildFetchParams = useCallback(
    (page = currentPage) => {
      const p = { page, per_page: pageSize };
      if (filters.period) p.period = filters.period;
      if (searchQuery) p.q = searchQuery;
      if (filters.sales_units.length) p.sales_unit = filters.sales_units.join(',');
      if (filters.status) p.status = filters.status;
      const sortModel = salesState.sortParams;
      if (Array.isArray(sortModel) && sortModel.length) {
        p.sort_field = sortModel[0].colId;
        p.sort_dir   = sortModel[0].sort;
      }
      return p;
    },
    [currentPage, pageSize, salesState.sortParams, filters, searchQuery]
  );

  const reload = useCallback(
    (page = currentPage) => {
      dispatch(getSalesForecasts({ params: buildFetchParams(page) }));
    },
    [dispatch, buildFetchParams, currentPage]
  );

  const handleExpandAll = useCallback(() => {
    gridRef.current?.api?.forEachNode((node) => node.setExpanded(true));
    setAllExpanded(true);
  }, []);

  const handleCollapseAll = useCallback(() => {
    gridRef.current?.api?.forEachNode((node) => node.setExpanded(false));
    setAllExpanded(false);
  }, []);

  // filters change always resets to page 1
  useEffect(() => {
    setCurrentPage(1);
    reload(1);
    // only on filter/search change: reload also changes with currentPage, which would snap every page change back to 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchQuery]);

  // page / pageSize changes trigger a server fetch (skip initial mount to avoid double-fetch)
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    reload(currentPage);
    // only on page/size change: reload also changes with filters, whose effect above already fetches page 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    clearSearch();
    setFilters((prev) => ({ ...prev, sales_units: [], status: '' }));
  };

  const hasActiveFilters =
    searchInput !== '' ||
    filters.sales_units.length > 0 ||
    filters.status !== '';

  const handleEdit = useCallback((record) => {
    setSelectedRecord(record);
    setShowEditForm(true);
  }, []);

  const handleDelete = useCallback(
    (record) => {
      confirmAlert({
        title: 'Confirmation',
        message: `Are you sure you want to delete <strong class="tw-font-bold tw-text-blue-500">${record.project.name}</strong> this Sales Forecast record? This action cannot be undone.`,
        closeOnEscape: true,
        closeOnClickOutside: false,
        buttons: [
          {
            label: 'Yes',
            onClick: async () => {
              try {
                await dispatch(deleteSalesForecast({ saleIds: [record.id] })).unwrap();
                reload(1);
                toast.success('Deleted successfully');
              } catch (error) {
                toast.error('Failed to delete record');
              }
            }
          },
          {
            label: 'No',
            onClick: () => {}
          }
        ]
      });
    },
    [dispatch, reload]
  );

  const handleReleaseClick = useCallback((record) => { setReleaseTarget(record); }, []);

  const handleReleaseConfirm = useCallback(
    ({ toEmails, ccEmails, saleIds, subject, body }) => {
      setReleaseTarget(null);
      dispatch(releaseSalesToCsd({ toEmails, ccEmails, saleIds, subject, body }))
        .unwrap()
        .then(() => { reload(); })
        .catch(() => {});
    },
    [dispatch, reload]
  );

  const handleBulkDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) return;
    const deletableCount = selectedRows.filter((r) => !r.sent_at).length;
    if (deletableCount === 0) {
      toast.error('Cannot delete sent records.');
      return;
    }

    confirmAlert({
      title: 'Confirmation',
      message: `Are you sure you want to delete <strong>${deletableCount}</strong> selected Sales Forecast record(s)? This action cannot be undone.`,
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Yes',
          onClick: async () => {
            try {
              const ids = selectedRows.filter((r) => !r.sent_at).map((r) => r.id);
              dispatch(deleteSalesForecast({ saleIds: ids })).then(() => {
              setSelectedRows([]);
              reload(1);
              toast.success('Deleted successfully');
            });
          } catch (error) {
              toast.error('Failed to delete records');
            }
          }
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  }, [selectedRows, dispatch, reload]);

  const firstRow = salesState.allData?.data?.[0];
  const monthLabels = useMemo(() => {
    if (!firstRow?.buildings?.length) return getForecastMonthLabels(firstRow?.period);
    const unique = {};
    firstRow.buildings.forEach(b => {
      if (b.month && !unique[b.month]) unique[b.month] = b.month_label;
    });
    return [1, 2, 3].map(m => unique[m] || getForecastMonthLabels(firstRow?.period)[m - 1]);
  }, [firstRow?.buildings, firstRow?.period]);

  const detailCellRendererParams = useMemo(() => ({
    detailGridOptions: {
      columnDefs: [
        { headerName: 'Building', field: 'name', flex: 1, minWidth: 120 },
        {
          headerName: 'Month', field: 'month_label', width: 150,
          cellRenderer: (p) => p.value
            ? <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-bg-blue-100 tw-text-blue-700 tw-rounded-sm tw-text-xs tw-font-semibold">{p.value}</span>
            : <span className="tw-text-gray-400 tw-text-xs">&mdash;</span>,
        },
        {
          headerName: 'Weight (MT)', field: 'weight', width: 150, type: 'numericColumn',
          valueFormatter: (p) => (p.value != null ? Number(p.value).toFixed(2) : '\u2014'),
        },
        { headerName: 'Status', field: 'status', flex: 1, minWidth: 160, cellRenderer: (p) => p.value ? <Badge className="tw-bg-blue-50 tw-text-blue-600 tw-text-xs tw-font-medium">{p.value}</Badge> : null },
        { headerName: 'Remark', field: 'remark', flex: 1, minWidth: 160 },
      ],
      defaultColDef: { sortable: true, resizable: true },
      domLayout: 'autoHeight',
      suppressMovableColumns: true,
      headerHeight: 36,
      rowHeight: 36,
      getRowStyle: () => ({ fontSize: '12px' }),
    },
    getDetailRowData: (params) => { params.successCallback(params.data?.buildings || []); },
  }), []);

  const columnDefs = useMemo(() => [
    { cellRenderer: 'agGroupCellRenderer', width: 40, suppressSizeToFit: true, resizable: false, sortable: false, pinned: 'left' },
    { checkboxSelection: true, headerCheckboxSelection: true, width: 48, suppressSizeToFit: true, resizable: false, sortable: false, pinned: 'left' },
    { headerName: 'Period', field: 'period', width: 110, sortable: true, pinned: 'left', cellStyle: { textAlign: 'center', fontWeight: 500 } },
    {
      headerName: 'Quote/Job no.',
      cellRenderer: (p) => (
        <Link
          to={`/crm/projects/${p.data?.project?.id}`}
          target="_blank"
          className="tw-text-blue-600 hover:tw-text-blue-800 tw-no-underline"
        >
          {p.data?.project?.j_number || p.data?.project?.q_number || '—'}
        </Link>
      ),
      sortable: false, flex: 1, minWidth: 120
    },
    {
      // The row is `{ id, ...attributes }`; SaleSerializer exposes the project as a
      // nested object, so there is no flat `project_name` to bind to. colId stays
      // `project_name` because onSortChanged maps it to the server sort field.
      headerName: 'Project',
      colId: 'project_name',
      valueGetter: (p) => p.data?.project?.name || '',
      sortable: true,
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: 'Customer',
      cellRenderer: (p) => (
        <Link
          to={`/crm/customers/${p.data?.project?.customer_id}`}
          target="_blank"
          className="tw-text-blue-600 hover:tw-text-blue-800 tw-no-underline"
        >
          {p.data?.project?.customer_name || '—'}
        </Link>
      ),
      sortable: false,
      minWidth: 180,
      flex: 1,
    },
    {
      headerName: 'Sales Incharge',
      cellRenderer: (p) => {
        const name = p.data?.project?.sales_incharge?.toUpperCase();
        return (
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-full">
            <Avatar initials={name} size="sm" variant="primary" />
          </div>
        );
      },
      cellStyle: { textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' },
      sortable: false, minWidth: 160, flex: 1,
    },
    { headerName: 'Tentative Date \n of RAD', field: 'tentative_date_of_rad', sortable: true, width: 160, wrapHeaderText: true, autoHeaderHeight: true, valueFormatter: (p) => (p.value ? formatDate(p.value) : '\u2014') },
    {
      headerName: 'Tentative Date of Contract',
      field: 'tentative_date_of_contract',
      sortable: true,
      width: 260,
      cellRenderer: (p) => {
        if (p.value) {
          return formatDate(p.value);
        }
        if (p.data?.tentative_date_of_contract_note) {
          return <span className="tw-text-red-500">{p.data.tentative_date_of_contract_note}</span>;
        }
        return '\u2014';
      }
    },
    {
      headerName: '% Chance \n of Winning', field: 'percent_chance_of_winning', sortable: true, width: 140, wrapHeaderText: true, autoHeaderHeight: true,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 600, color: p.value > 70 ? '#15803d' : p.value > 40 ? '#92400e' : p.value != null ? '#991b1b' : '#94a3b8' }),
      valueFormatter: (p) => (p.value != null ? `${p.value}%` : '\u2014'),
    },
    { headerName: 'Expected Date \n of Release Fabrication', field: 'expected_date_release_fabrication', sortable: true, width: 155, wrapHeaderText: true, autoHeaderHeight: true, valueFormatter: (p) => (p.value ? formatDate(p.value) : '\u2014') },
    {
      headerName: '% Chance \n of Release', field: 'percent_change_of_release', sortable: true, width: 140, wrapHeaderText: true, autoHeaderHeight: true,
      cellStyle: (p) => ({ textAlign: 'center', fontWeight: 600, color: p.value > 70 ? '#15803d' : p.value > 40 ? '#92400e' : p.value != null ? '#991b1b' : '#94a3b8' }),
      valueFormatter: (p) => (p.value != null ? `${p.value}%` : '\u2014'),
    },
    {
      headerName: 'MT Released for Fabrication',
      headerClass: 'mt-released-group',
      headerCellStyle: { fontWeight: 600, backgroundColor: '#f3f4f6' },
      cellStyle: { fontWeight: 600, backgroundColor: '#dbeafe' },
      children: [
        { headerName: monthLabels[0], headerClass: 'mt-released-child', width: 140, sortable: false, type: 'numericColumn', valueGetter: (p) => monthTotal(p.data?.buildings, 1), valueFormatter: (p) => (p.value ? p.value.toFixed(2) : '\u2014'), headerStyle: getMonthColorStyles(0).headerStyle, cellStyle: { ...getMonthColorStyles(0).cellStyle, color: '#1d4ed8', textAlign: 'center', fontWeight: 500 } },
        { headerName: monthLabels[1], headerClass: 'mt-released-child', width: 140, sortable: false, type: 'numericColumn', valueGetter: (p) => monthTotal(p.data?.buildings, 2), valueFormatter: (p) => (p.value ? p.value.toFixed(2) : '\u2014'), headerStyle: getMonthColorStyles(1).headerStyle, cellStyle: { ...getMonthColorStyles(1).cellStyle, color: '#1d4ed8', textAlign: 'center', fontWeight: 500 } },
        { headerName: monthLabels[2], headerClass: 'mt-released-child', width: 140, sortable: false, type: 'numericColumn', valueGetter: (p) => monthTotal(p.data?.buildings, 3), valueFormatter: (p) => (p.value ? p.value.toFixed(2) : '\u2014'), headerStyle: getMonthColorStyles(2).headerStyle, cellStyle: { ...getMonthColorStyles(2).cellStyle, color: '#1d4ed8', textAlign: 'center', fontWeight: 500 } },
      ],
    },
    {
      headerName: 'Status', field: 'sent_at', sortable: true, width: 110,
      cellRenderer: (p) => <StatusBadge status={p.value ? 'released' : 'draft'} />,
    },
    {
      headerName: "Released date",
      field: 'sent_at',
      width: 150,
      valueFormatter: (p) => (p.value ? formatDate(p.value) : '\u2014'),
    },
    {
      headerName: 'Released by',
      field: 'sent_by',
      width: 110,
      cellRenderer: (p) => {
        const name = p.value?.toUpperCase();
        return (
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-full">
            {name && name !== '\u2014' ? <Avatar initials={name} size="sm" variant="info" /> : <span>{p.value}</span>}
          </div>
        );
      },
      valueGetter: (p) => p.data?.sent_by || (p.data?.sent_at ? 'System' : '\u2014'),
    },
    {
      headerName: 'Sales unit', field: 'sales_unit.name', sortable: true, width: 140,
    },
    {
      headerName: 'Sales region', field: 'sales_unit.region', sortable: true, width: 180,
    },
    {
      headerName: 'Project status',
      field: 'project_status_name',
      sortable: true,
      width: 150,
      cellRenderer: (p) => <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-blue-50 tw-text-blue-600 tw-text-xs tw-font-medium tw-border tw-border-blue-100">
                {p.value}
              </span>
    },
    {
      headerName: 'Remark',
      field: 'remark',
      width: 200,
      cellRenderer: (params) => <span className='tw-text-red-500'>{params.value}</span>,
    },
    {
      headerName: 'Created by',
      field: 'created_by',
      width: 130,
      cellRenderer: (p) => {
        const name = p.value.toUpperCase();
        return (
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-h-full">
            <Avatar initials={name} size="sm" variant="primary" />
          </div>
        );
      },
    },
    {
      headerName: 'Created date',
      field: 'created_date',
      width: 180
    },
    {
      headerName: 'Actions', width: 110, sortable: false, filter: false, pinned: 'right',
      cellRenderer: ActionCell,
      cellRendererParams: { onEdit: handleEdit, onDelete: handleDelete },
    },
  ], [monthLabels, handleEdit, handleDelete]);

  const onSortChanged = useCallback((e) => {
    const sortModel =
      e.api.getSortModel?.() ||
      e.columnApi?.getColumnState()?.filter((c) => c.sort)?.map((c) => ({ colId: c.colId, sort: c.sort })) ||
      [];
    if (sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      const fieldMap = { period: 'period', project_name: 'project_name', tentative_date_of_rad: 'tentative_date_of_rad', tentative_date_of_contract: 'tentative_date_of_contract', percent_chance_of_winning: 'percent_chance_of_winning', percent_change_of_release: 'percent_change_of_release', sent_at: 'sent_at' };
      const sortField = fieldMap[colId];
      if (sortField) { dispatch(setSalesSort([{ colId: sortField, sort }])); reload(1); }
    }
  }, [dispatch, reload]);

  const handleExportExcel = useCallback(() => {
    dispatch(exportSalesForecast(buildFetchParams()));
  }, [dispatch, buildFetchParams]);

  const rows        = salesState.allData?.data || [];
  const totalRows   = salesState.allData?.totalRows || 0;
  const totalPages  = Math.max(1, Math.ceil(totalRows / pageSize));

  const deletableCount = selectedRows.filter((r) => !r.sent_at).length;
  const unsentCount    = rows.filter((r) => !r.sent_at).length;

  useEffect(() => { setPageReady(true); }, []);

  if (!pageReady) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-min-h-screen tw-bg-gray-50 tw-gap-4">
        <LoadingSpinner size="lg" />
        <p className="tw-text-sm tw-text-gray-500 tw-animate-pulse">Loading Release Forecast Sales…</p>
      </div>
    );
  }

  return (
    <div className="tw-px-4 tw-py-4 tw-bg-gray-50 tw-min-h-screen">
      <style>{headerGroupStyle}</style>
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
        <div className="tw-w-1.5 tw-h-10" style={{ background: 'linear-gradient(to bottom, #2563eb, #60a5fa)', borderRadius: 3 }} />
        <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">3-Month Sales Release Forecast</h1>
      </div>
      <p className="tw-text-gray-500 tw-text-sm tw-mb-4 tw-ml-7">Manage monthly fabrication release forecasts per project</p>

      <div className="tw-rounded tw-border tw-border-gray-200 tw-shadow-sm tw-mb-3 tw-bg-white">
        {/* ── Combined Filter + Action bar ── */}
        <div className="tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-1.5 tw-overflow-x-auto tw-border-b tw-border-gray-100">
          {/* Filter section */}
          <div className="tw-flex tw-items-end tw-gap-1.5 tw-flex-shrink-0">
            {/* Period */}
            <div className="tw-flex tw-flex-col tw-flex-shrink-0">
              <label className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1 tw-flex tw-items-center tw-gap-1">
                <FaCalendarAlt size={10} className="tw-text-blue-500" /> Period
              </label>
              <MonthYearPicker
                value={filters.period}
                onChange={(val) => setFilter('period', val)}
                placeholder="All months"
                clearable
                minWidth={120}
                size="sm"
              />
            </div>

            {/* Combined project / quote / job search */}
            <div className="tw-flex tw-flex-col tw-flex-shrink-0">
              <label className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1">Project / Quote / Job</label>
              <div className="tw-relative">
                <FaSearch size={10} className="tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search project, QB/P/J number…"
                  className="tw-h-8 tw-border tw-border-gray-300 tw-rounded tw-pl-7 tw-pr-3 tw-text-xs tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-200 tw-transition-colors"
                  style={{ width: 250 }}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </div>

            {/* Sales Unit filter — inline in main bar */}
            <div className="tw-flex tw-flex-col tw-flex-shrink-0">
              <label className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1">Sales Unit</label>
              <SalesUnitSelect
                value={filters.sales_units}
                onChange={(v) => setFilter('sales_units', v)}
                width={190}
                minHeight={30}
              />
            </div>

            {/* Status filter */}
            <div className="tw-flex tw-flex-col tw-flex-shrink-0">
              <label className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1">Status</label>
              <StatusSelect
                value={filters.status}
                onChange={(v) => setFilter('status', v)}
                width={130}
              />
            </div>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                type="button"
                className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-xs tw-font-medium tw-border tw-border-gray-200 tw-bg-white tw-text-gray-500 hover:tw-bg-red-50 hover:tw-text-red-600 hover:tw-border-red-200 tw-cursor-pointer tw-transition-colors tw-flex-shrink-0"
                onClick={clearFilters}
                title="Clear all filters"
              >
                <FaTimes size={8} />
              </button>
            )}
          </div>

          {/* Action section */}
          <div className="tw-flex tw-items-center tw-gap-1 tw-ml-auto tw-flex-shrink-0">
            <Button onClick={() => reload()} disabled={salesState.loading} size="sm" className="tw-px-2" title="Refresh">
              <FaSync className={salesState.loading ? 'tw-animate-spin tw-inline' : 'tw-inline'} size={9} /> Reload
            </Button>
            <Button
              onClick={allExpanded ? handleCollapseAll : handleExpandAll}
              size="sm"
              variant="secondary"
              title={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
              className="tw-px-2 tw-text-xs"
            >
              {allExpanded ? '⊟ Collapse All' : '⊞ Expand All'}
            </Button>
            {deletableCount > 0 && (
              <Button variant="danger" onClick={handleBulkDeleteClick} size="sm" className="tw-text-xs" title={`Delete ${deletableCount} record(s)`}>
                <FaTrash className="tw-inline" size={9} />
                ({deletableCount}) Delete
              </Button>
            )}
            <Button onClick={() => setShowSelectModal(true)} disabled={salesState.loading} size="sm" className="tw-text-xs">
              <FaPlus className="tw-inline" size={9} />
              New
            </Button>
            <Button onClick={handleReleaseClick} disabled={salesState.loading || unsentCount === 0} size="sm" className="tw-text-xs" title={unsentCount === 0 ? 'No unsent records' : 'Release to CSD'}>
              <FaPaperPlane className="tw-inline tw-bg-orange-500 tw-text-white tw-rounded-xl" size={9} />
              Release
            </Button>
            <Button
              onClick={handleExportExcel}
              disabled={salesState.loading || exportLoading}
              className="tw-px-2"
              size="sm"
              title={exportLoading ? 'Exporting...' : 'Export to Excel'}
            >
              {exportLoading ? (
                <FaSync className="tw-animate-spin tw-inline" size={9} />
              ) : (
                <FaDownload className="tw-inline" size={9} />
              )} Export
            </Button>
          </div>
        </div>

      </div>

      {/* Summary Footer (Server-side totals) */}
      {salesState.allData?.summary && (
        <div className="tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-md tw-bg-white tw-overflow-hidden tw-mb-5">
          <div className="tw-px-4 tw-py-3 tw-bg-gradient-to-r tw-from-primary-50 tw-to-blue-50 tw-border-b tw-border-gray-200">
            <h3 className="tw-text-xs tw-font-bold tw-text-gray-900 tw-uppercase tw-tracking-wider tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <span className="tw-w-1 tw-h-4 tw-bg-primary-500 tw-rounded-xl" />
              Summary (All Records)
            </h3>
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-2">
              {/* Total Projects Card */}
              <div className="tw-rounded tw-bg-white tw-border tw-border-blue-100 tw-p-3 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-flex-col">
                  <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-1">Total Projects</div>
                  <div className="tw-font-bold tw-text-xl tw-text-primary-600">{salesState.allData?.summary?.project_count || 0}</div>
                </div>
              </div>

              {/* Grand Total Card */}
              <div className="tw-rounded tw-bg-white tw-border tw-border-green-100 tw-p-3 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-flex-col">
                  <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-1">Grand Total</div>
                  <div className="tw-font-bold tw-text-xl tw-text-green-700">
                    {(formatNumber((salesState.allData?.summary?.month1_total || 0) +
                      (salesState.allData?.summary?.month2_total || 0) +
                      (salesState.allData?.summary?.month3_total || 0)))}
                  </div>
                  <div className="tw-text-xs tw-text-green-600 tw-font-semibold">MT</div>
                </div>
              </div>

              {/* Month 1 Card */}
              <div className="tw-rounded tw-bg-white tw-border tw-border-purple-100 tw-p-3 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-flex-col">
                  <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-0.5">Month 1</div>
                  <div className="tw-text-xs tw-text-gray-500 tw-font-medium tw-mb-1">{monthLabels[0]}</div>
                  <div className="tw-font-bold tw-text-xl tw-text-purple-700">{formatNumber(salesState.allData?.summary?.month1_total || 0)}</div>
                  <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">MT</div>
                </div>
              </div>

              {/* Month 2 Card */}
              <div className="tw-rounded tw-bg-white tw-border tw-border-cyan-100 tw-p-3 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-flex-col">
                  <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-0.5">Month 2</div>
                  <div className="tw-text-xs tw-text-gray-500 tw-font-medium tw-mb-1">{monthLabels[1]}</div>
                  <div className="tw-font-bold tw-text-xl tw-text-cyan-700">{formatNumber(salesState.allData?.summary?.month2_total || 0)}</div>
                  <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">MT</div>
                </div>
              </div>

              {/* Month 3 Card */}
              <div className="tw-rounded tw-bg-white tw-border tw-border-orange-100 tw-p-3 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
                <div className="tw-flex tw-flex-col">
                  <div className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide tw-mb-0.5">Month 3</div>
                  <div className="tw-text-xs tw-text-gray-500 tw-font-medium tw-mb-1">{monthLabels[2]}</div>
                  <div className="tw-font-bold tw-text-xl tw-text-orange-700">{formatNumber(salesState.allData?.summary?.month3_total || 0)}</div>
                  <div className="tw-text-xs tw-text-gray-400 tw-mt-0.5">MT</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="tw-rounded tw-border tw-border-gray-200 tw-shadow-sm tw-bg-white tw-overflow-hidden tw-relative">
        {salesState.loading && <LoadingSpinner overlay message="Loading…" />}
        {exportLoading && <LoadingSpinner overlay message="Exporting to Excel…" />}
        <div className="ag-theme-quartz" style={{ height: 580, '--ag-font-size': '12px' }}>
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={rows}
            rowSelection="multiple"
            suppressRowClickSelection
            animateRows
            masterDetail
            detailRowAutoHeight
            isRowMaster={(data) => (data?.buildings?.length ?? 0) > 0}
            detailCellRendererParams={detailCellRendererParams}
            onSortChanged={onSortChanged}
            onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
            getRowId={(p) => String(p.data.id)}
            getRowStyle={(p) => ({ fontSize: '12px', ...(p.data?.sent_at ? { background: '#f0fdf4' } : {}) })}
          />
        </div>

        {/* Pagination */}
        <div className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-rounded tw-px-4 tw-py-2.5 tw-shadow-sm tw-border tw-border-gray-200">
          <span className="tw-text-xs tw-text-gray-500">
            Records: <span className="tw-font-semibold tw-text-gray-800">{totalRows.toLocaleString()}</span>
          </span>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalResults={totalRows}
            perPage={pageSize}
            onPageChange={(pg) => setCurrentPage(pg)}
            onPageSizeChange={(sz) => { setPageSize(sz); setCurrentPage(1); }}
            disabled={salesState.loading}
            pageSizeOptions={[20, 50, 100, 200]}
          />
        </div>
      </div>

      <SalesBuildingSelectModal show={showSelectModal} onHide={() => setShowSelectModal(false)} onSuccess={() => reload(1)} defaultPeriod={filters.period} />

      <SalesForecastEditForm
        show={showEditForm}
        onHide={() => { setShowEditForm(false); setSelectedRecord(null); }}
        record={selectedRecord}
        onSaved={() => reload()}
      />

      {releaseTarget && (() => {
        const explicitIds = selectedRows.filter(r => !r.sent_at).map(r => r.id);
        const allUnsentIds = rows.filter(r => !r.sent_at).map(r => r.id);
        const idsToRelease = explicitIds.length > 0 ? explicitIds : allUnsentIds;
        return (
          <ReleaseConfirmDialog
            forecastPeriod={filters.period}
            selectedIds={idsToRelease}
            selectedCount={explicitIds.length > 0 ? unsentCount : null}
            onConfirm={handleReleaseConfirm}
            onCancel={() => setReleaseTarget(null)}
          />
        );
      })()}
    </div>
  );
};

export default ReleaseForecastSalesPage;
